import { z } from "zod";

/**
 * Ausfuhrbegleitdokument (ABD): Ausfuhren in Drittländer (außerhalb der EU) ab einem Warenwert von 1.000 € oder
 * einem Gewicht von 1.000 kg brauchen eine Ausfuhranmeldung. Solche Aufträge hält Connect zurück, bis das Dokument
 * hochgeladen und freigegeben ist; danach geht es mit dem Auftrag als Anhang ans Lager.
 */
export const EU_COUNTRIES = new Set(["AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK"]);

export const exportDocumentSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  /** Warenwert ab (EUR, inklusive) */
  valueThresholdEur: z.number().min(0).max(1_000_000).default(1000),
  /** Gesamtgewicht ab (kg, inklusive) */
  weightThresholdKg: z.number().min(0).max(1_000_000).default(1000),
});
export type ExportDocumentSettings = z.infer<typeof exportDocumentSettingsSchema>;

export const EXPORT_DOC_HOLD_PREFIX = "Ausfuhrbegleitdokument";
export const EXPORT_DOC_MISSING = "Ausfuhrbegleitdokument fehlt";
export const EXPORT_DOC_PENDING = "Ausfuhrbegleitdokument wartet auf Freigabe";

/**
 * Braucht der Auftrag ein ABD? Nur Drittländer. Warenwert: Summe der Positionen (ohne Versand), sonst Auftragswert.
 * Andere Währung als EUR: Betrag wird vorsichtshalber wie EUR verglichen (lieber einmal zu viel zurückhalten).
 */
export function exportDocumentNeed(p: { country: string; valueMinor: number | null; currency: string; weightKg: number }, s: ExportDocumentSettings): { needed: boolean; reasons: string[] } {
  const country = (p.country ?? "").toUpperCase();
  if (!s.enabled || !country || EU_COUNTRIES.has(country)) return { needed: false, reasons: [] };
  const reasons: string[] = [];
  const value = (p.valueMinor ?? 0) / 100;
  if (value >= s.valueThresholdEur) reasons.push(`Warenwert ${value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${p.currency || "EUR"} ≥ ${s.valueThresholdEur.toLocaleString("de-DE")} €`);
  if (p.weightKg >= s.weightThresholdKg) reasons.push(`Gewicht ${p.weightKg.toLocaleString("de-DE", { maximumFractionDigits: 1 })} kg ≥ ${s.weightThresholdKg.toLocaleString("de-DE")} kg`);
  return { needed: reasons.length > 0, reasons };
}

/** Haltegrund je Stand: fehlt (hochladen) oder hochgeladen, wartet auf Freigabe */
export function exportDocHoldReason(state: "missing" | "pending", country: string, reasons: string[]): string {
  return state === "missing"
    ? `${EXPORT_DOC_MISSING}: Ausfuhr nach ${country.toUpperCase()} (${reasons.join(", ")}) – Dokument im Auftrag hochladen und freigeben`
    : `${EXPORT_DOC_PENDING}: Dokument ist hochgeladen – bitte prüfen und im Auftrag freigeben`;
}
